import { CheckCircle2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TemplateMediaAsset } from '@/lib/templateComposer';

// A media item's generated photos or videos as a row of thumbnails to pick one from.
//
// Thumbnails rather than a dropdown of labels, for the same reason the campaign wizard uses cards:
// the thing being chosen is a picture, and "Option 2 — photo-1" is a poor way to recognise one.
// Used by the template composer (which picture WhatsApp reviews) and the template's detail dialog
// (which picture campaigns start with).

interface MediaAssetGridProps {
  assets: TemplateMediaAsset[];
  chosenUid: string | null;
  onPick: (asset: TemplateMediaAsset) => void;
  /** Names the group for screen readers, e.g. "Header image". */
  label: string;
  /** The chosen asset is still being worked on — a spinner instead of a tick. */
  busy?: boolean;
  disabled?: boolean;
}

export default function MediaAssetGrid({
  assets, chosenUid, onPick, label, busy = false, disabled = false,
}: MediaAssetGridProps) {
  const refused = assets.find((asset) => !asset.usable);

  return (
    <div className="space-y-2">
      <div role="radiogroup" aria-label={label} className="grid grid-cols-3 gap-2">
        {assets.map((asset) => {
          const chosen = asset.uid === chosenUid;
          return (
            <button
              key={asset.uid}
              type="button"
              role="radio"
              aria-checked={chosen}
              aria-label={asset.label}
              title={asset.reason || asset.label}
              disabled={disabled || !asset.usable}
              onClick={() => onPick(asset)}
              className={cn(
                'relative overflow-hidden rounded-md border bg-muted text-start transition-all',
                'outline-none focus-visible:ring-2 focus-visible:ring-ring',
                chosen ? 'border-primary ring-2 ring-primary' : 'hover:border-primary/60',
                !asset.usable && 'cursor-not-allowed opacity-50',
              )}
            >
              {asset.url && asset.kind === 'photo' && (
                <img src={asset.url} alt="" className="aspect-square w-full object-cover" />
              )}
              {asset.url && asset.kind === 'video' && (
                <video
                  src={asset.url}
                  className="aspect-square w-full bg-black object-cover"
                  muted
                  playsInline
                  preload="metadata"
                />
              )}
              {!asset.url && (
                <span className="flex aspect-square w-full items-center justify-center p-1 text-center text-[10px] text-muted-foreground">
                  {asset.label}
                </span>
              )}
              {chosen && (
                <span className="absolute end-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                </span>
              )}
              {asset.selected && (
                // The media's final selection in the builder — what the user already said they want.
                <span className="absolute bottom-1 start-1 rounded bg-background/85 px-1 text-[10px]">
                  Selected
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Offered but greyed out, with the reason — hiding a picture leaves somebody looking for it. */}
      {refused && <p className="text-xs text-muted-foreground">{refused.reason}</p>}
    </div>
  );
}
